import { Form } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useMemo } from "react"

import { AddressMapPreview } from "../../../../../components/Shared/AddressMapPreview.tsx"
import { zipCodeRegexUS } from "../../../../../utils/validation/zip-code.ts"
import { type ProjectFormData } from "../project-form-data.ts"

interface ProjectAddressMapPreviewProps {
  form: FormInstance<ProjectFormData>
  isCustomerSection?: boolean
}

export function ProjectAddressMapPreview({
  form,
  isCustomerSection
}: ProjectAddressMapPreviewProps) {
  const addressData = Form.useWatch((state) => {
    if (isCustomerSection) {
      return {
        address: state?.customer?.address,
        state: state?.customer?.state,
        city: state?.customer?.city,
        zip_code: state?.customer?.zip_code
      }
    }

    return {
      address: state?.address,
      state: state?.state,
      city: state?.city,
      zip_code: state?.zip_code
    }
  }, form)

  const { fullAddress, emptyText } = useMemo(() => {
    const address = addressData?.address?.trim()
    const city = addressData?.city?.trim()
    const state = addressData?.state?.trim()
    const rawZipCode = addressData?.zip_code?.trim().toUpperCase()

    const hasAnyInput = address || city || state || rawZipCode
    const isValidZipCode = rawZipCode && zipCodeRegexUS.test(rawZipCode)
    const zipCode = isValidZipCode ? rawZipCode : null

    const issues: string[] = []

    if (rawZipCode && !isValidZipCode) {
      issues.push("invalid ZIP code")
    }

    if (!city && (address || state || zipCode)) {
      issues.push("city is required")
    }

    if (!state && (address || city || zipCode)) {
      issues.push("state is required")
    }

    if (issues.length > 0) {
      return {
        fullAddress: "",
        emptyText: `Cannot show map: ${issues.join(", ")}`
      }
    }

    if (!hasAnyInput) {
      return {
        fullAddress: "",
        emptyText: "Enter address to see map preview"
      }
    }

    if (!city || !state) {
      return {
        fullAddress: "",
        emptyText: "Enter at least city and state to see map preview"
      }
    }

    const fullAddr = [address, city, state, zipCode].filter(Boolean).join(", ")

    return {
      fullAddress: `${fullAddr}, USA`,
      emptyText: ""
    }
  }, [addressData])

  return (
    <AddressMapPreview
      fullAddress={fullAddress || null}
      emptyText={emptyText}
    />
  )
}
